import { Injectable, signal } from '@angular/core';
import {
  CheckboxFilter,
  ChipsFilter,
  DateRangeFilter,
  FilterCriteria,
  FilterType,
  InputFilter,
  MultiselectFilter,
  SearchCriteriaCursor,
} from '@shared/components/filters/Domain/filter.model';
import { TranslateService } from '@ngx-translate/core';
import { mapOutboundCallStatusToFilterOptions } from '@modules/calls/Application/outboundCall.utils';
import { SearchOrigin } from '@shared/components/filters/Domain/search-method.enum';
import { FilterStorageService } from '@shared/components/filters/Application/Services/filter-storage.service';
import { ActivatedRoute } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class OutboundCallFacade {
  loading = signal<boolean>(false);
  lastCriteria = signal<FilterCriteria>({});
  actualFirst = signal<number>(0);
  numResultsDisplayed = signal<number>(10);

  key = 'call-filters';

  constructor(
    private translate: TranslateService,
    private filterService: FilterStorageService,
    private router: ActivatedRoute,
  ) {}

  getFilters(): (
    | MultiselectFilter
    | InputFilter
    | DateRangeFilter
    | ChipsFilter
    | CheckboxFilter
  )[] {
    return [
      {
        id: 'customerCallsExternalIds',
        label: 'IDs de llamada',
        type: FilterType.Chips,
        icon: 'pi pi-phone',
        tooltip: this.translate.instant('DATA.MODULES.INSTANCES.LIST.FORM.SEARCH_TOOLTIP_TYPE', {
          message: 'Ids de llamada',
        }),
      } as ChipsFilter,
      {
        id: 'customerExternalIds',
        label: 'IDs de cliente',
        type: FilterType.Chips,
        icon: 'pi pi-users',
        tooltip: this.translate.instant('DATA.MODULES.INSTANCES.LIST.FORM.SEARCH_TOOLTIP_TYPE', {
          message: 'Ids de cliente',
        }),
      } as ChipsFilter,
      {
        id: 'phonesCalls',
        label: this.translate.instant('DATA.MODULES.CUSTOMERS.PAGES.CUSTOMER_LIST.FORM.PHONES'),
        type: FilterType.Chips,
        icon: 'pi pi-phone',
        tooltip: this.translate.instant('DATA.COMMON.FILTER.CHIPS', {
          nameFilter: 'Teléfonos de cliente',
          ejemplos: ' +34605398222, +34605398111 ... Debe buscar con el prefijo del país.',
        }),
      } as ChipsFilter,
      {
        id: 'callStatus',
        label: this.translate.instant('DATA.MODULES.OPERATIONS.PAGES.SEARCH.STATUS'),
        type: FilterType.Checkbox,
        icon: 'pi pi-bell',
        options: mapOutboundCallStatusToFilterOptions(this.translate),
      } as CheckboxFilter,
      {
        id: 'conversationResults',
        label: this.translate.instant(
          'DATA.MODULES.CUSTOMERS.PAGES.CUSTOMER_LIST.FORM.CONVERSATIONS_RESULTS',
        ),
        type: FilterType.Input,
        placeholder: 'Enter a title',
        icon: 'pi pi-comment',
        tooltip:
          'Introduce palabras completas de 3 o más caracteres, no parciales. Puedes buscar varias palabras separadas por espacios, guiones o puntos.',
      } as InputFilter,
      {
        id: 'createdAtDateInterval',
        label: this.translate.instant('DATA.MODULES.CUSTOMERS.PAGES.CUSTOMER_LIST.FORM.CALL_TIME'),
        type: FilterType.DateRange,
        icon: 'pi pi-calendar',
        minDate: '2020-01-01',
        maxDate: '2030-12-31',
        tooltip: this.translate.instant('DATA.MODULES.INSTANCES.LIST.FORM.RANGE_TOOLTIP'),
      } as DateRangeFilter,
    ];
  }

  resolveSearchCriteria(
    event: FilterCriteria | undefined,
    origin: SearchOrigin,
  ): SearchCriteriaCursor {
    const saved = this.filterService.load(this.key);

    let filtersToUse: FilterCriteria;

    if (origin === SearchOrigin.FILTER_CHANGE || origin === SearchOrigin.RESET_OR_RELOAD) {
      filtersToUse = event ?? {};
    } else if (origin === SearchOrigin.LOAD_NEXT) {
      filtersToUse = saved ?? {};
    } else {
      filtersToUse = event ?? saved ?? {};
    }

    const criteria = this.buildOutboundCallCriteria(filtersToUse, {
      offset: this.actualFirst() || 0,
      limit: this.numResultsDisplayed() || 10,
    });

    this.lastCriteria.set(filtersToUse);

    if (origin === SearchOrigin.FILTER_CHANGE) {
      this.actualFirst.set(0);
      this.filterService.save(this.key, filtersToUse);
    } else if (origin === SearchOrigin.RESET_OR_RELOAD) {
      this.actualFirst.set(0);
      this.filterService.clear(this.key);
      criteria.pagination = {
        cursor: null,
        limit: this.numResultsDisplayed(),
      };
    }

    return criteria;
  }

  private buildOutboundCallCriteria(
    filters?: FilterCriteria,
    pagination?: { offset: number; limit: number } | undefined,
  ): SearchCriteriaCursor {
    return {
      pagination: {
        cursor: pagination?.offset ? String(pagination.offset) : null,
        limit: pagination?.limit ?? this.numResultsDisplayed(),
      },
      filters: {
        tenantId: this.router.snapshot.queryParamMap.get('tenant'),
        externalIds: filters?.['customerCallsExternalIds'] ?? [],
        phones: filters?.['phonesCalls'] ?? [],
        statuses: filters?.['callStatus'] ?? [],
        customerExternalIds: filters?.['customerExternalIds'] ?? [],
        conversationResults: filters?.['conversationResults']
          ? Array.isArray(filters['conversationResults'])
            ? filters['conversationResults']
            : [filters['conversationResults']]
          : [],
        ...(filters?.['createdAtDateInterval']?.start &&
          filters?.['createdAtDateInterval']?.finish && {
            createdAtDateInterval: {
              start: filters['createdAtDateInterval'].start,
              finish: filters['createdAtDateInterval'].finish,
            },
          }),
        ...(filters?.['customerName'] && {
          customerName: filters?.['customerName'],
        }),
      },
    };
  }

  setPageSize(size: number) {
    if (this.numResultsDisplayed() === size) return;
    this.numResultsDisplayed.set(size);
  }
}
